// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentDetail.test.tsx
//
// The open comment's changed window in the drawer (Figma November 2026, frame
// 0c "Comment drawer"): the earlier and the newer cut side by side, and Show
// comparison to open the pair at that window.
import { type AskPunttComparisonRegion } from "@mg/schemas/src/christo/versionComparison";
import { SquareSplitHorizontal } from "@phosphor-icons/react";
import { Button, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { CompareSide } from "./AskV2ReviewCompareChanges";

export type CommentChange = {
  region: AskPunttComparisonRegion;
  beforeMark: string;
  afterMark: string;
};

export function AskV2ReviewCommentChange({
  commentId,
  change,
  onShowComparison,
}: {
  commentId: string;
  change: CommentChange;
  /** Absent, the cuts show without the way into the comparison. */
  onShowComparison?: (number: number) => void;
}) {
  const { t } = useTranslation("ui");
  const { region, beforeMark, afterMark } = change;
  const headingId = `ask-v2-review-comment-change-heading-${commentId}`;
  return (
    <section
      className="flex flex-col gap-2"
      data-testid={`ask-v2-review-comment-change-${commentId}`}
      data-window={region.number}
      aria-labelledby={headingId}
    >
      <Text
        as="p"
        size="1"
        weight="medium"
        id={headingId}
        className="text-puntt-neutral-gray-11"
      >
        {t("askV2.review.drawer.change.heading")}
      </Text>
      <div className="flex gap-2">
        {(["before", "after"] as const).map((side) => (
          <div
            key={side}
            className="min-w-0 flex-1 overflow-hidden rounded-md border border-puntt-neutral-gray-4"
          >
            <CompareSide
              region={region}
              side={side}
              mark={side === "before" ? beforeMark : afterMark}
            />
          </div>
        ))}
      </div>
      {onShowComparison ? (
        <Button
          variant="outline"
          color="gray"
          size="1"
          className="self-start"
          data-testid={`ask-v2-review-comment-change-show-${commentId}`}
          onClick={() => onShowComparison(region.number)}
        >
          <SquareSplitHorizontal size={14} aria-hidden="true" />
          {t("askV2.review.drawer.change.show")}
        </Button>
      ) : null}
    </section>
  );
}
